import * as styles from '../components/styles.scss';
import { getZoom, resetPanAndZoom, zoomIn, zoomOut, getNextZoomLevel } from '../viewstate';
import type { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { getTheme } from '@fluentui/react/lib/Styling';
import loc from 'owa-localize';
import { convertHotkeyStringToReadableText } from 'owa-readable-hotkeys';
import type * as React from 'react';
import {
    zoomInTooltip,
    zoomInAriaLabel,
    zoomOutTooltip,
    zoomOutAriaLabel,
    resetZoomLabel,
    resetZoomTooltip,
    resetZoomAriaLabel,
    zoomLevel,
} from './getZoomItems.locstring.json';

function preventDefaultAndCall(action: () => void) {
    return (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        ev && ev.preventDefault();
        action();
    };
}

const onZoomIn = preventDefaultAndCall(zoomIn);
const onZoomOut = preventDefaultAndCall(zoomOut);
const onResetPanAndZoom = preventDefaultAndCall(resetPanAndZoom);

export default function getZoomItems(isExpanded: boolean): ICommandBarItemProps[] {
    const { neutralSecondary } = getTheme().palette;
    const iconStyles = { root: { color: neutralSecondary } };
    const zoomInIcon = { iconName: 'ZoomIn', styles: iconStyles };

    const zoom = getZoom();
    const nextZoomInValue = getNextZoomLevel(zoom, 1) * 100;
    const nextZoomOutValue = getNextZoomLevel(zoom, -1) * 100;

    const zoomInEntry = {
        key: 'zoomIn',
        title: loc(zoomInTooltip, convertHotkeyStringToReadableText('ctrl+]')),
        ariaLabel: loc(zoomInAriaLabel, nextZoomInValue),
        iconOnly: true,
        iconProps: zoomInIcon,
        onClick: isExpanded ? onZoomIn : undefined, // no onClick as this is just a placeholder to make the command bar expand
        className: styles.zoomButton,
    };

    if (!isExpanded) {
        return [zoomInEntry];
    }

    // PERF: only compute the other buttons when expanded so we don't re-render the zoom tools
    // while performing a zoom operation with mouse or trackpad, otherwise the UI jitters.
    const zoomText = loc(zoomLevel, (zoom * 100).toString());
    const zoomOutIcon = { iconName: 'ZoomOut', styles: iconStyles };
    return [
        {
            key: 'resetZoom',
            name: loc(resetZoomLabel),
            ariaLabel: loc(resetZoomAriaLabel),
            title: loc(resetZoomTooltip, convertHotkeyStringToReadableText('ctrl+0')),
            onClick: onResetPanAndZoom,
            className: styles.resetZoom,
        },
        {
            key: 'zoomOut',
            title: loc(zoomOutTooltip, convertHotkeyStringToReadableText('ctrl+[')),
            ariaLabel: loc(zoomOutAriaLabel, nextZoomOutValue),
            iconOnly: true,
            iconProps: zoomOutIcon,
            onClick: onZoomOut,
        },
        {
            key: 'zoomLevel',
            name: zoomText,
            ariaLabel: zoomText,
            disabled: true,
            className: styles.zoomLevel,
        },
        zoomInEntry,
    ];
}
